/**
 * Onglet « Pédagogie » (API v2 `/pedagogy`). Aucune métrique n'est calculée
 * ici : tuiles, séries et tableaux sont affichés tels que le serveur les sert.
 */
import React from 'react';
import { Card, Col, Input, Row, Segmented, Space, Typography } from 'antd';
import {
    BookOutlined,
    CheckCircleOutlined,
    ExperimentOutlined,
    FieldTimeOutlined,
    LoginOutlined,
    ProjectOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    STUDENT_STATUSES,
    type PedagogyParams,
    type PedagogyResponse,
    type PedagogyStudentRow,
} from '../../../api/monitoring/v2';
import { cardStyle } from '../monitoringTheme';
import StatTile from '../shared/StatTile';
import CompactValue from '../shared/CompactValue';
import RatioText from '../shared/RatioText';
import EmptyState, { hasEmptyReason } from '../shared/EmptyState';
import TimeSeriesChart from '../shared/TimeSeriesChart';
import DataTable from '../shared/DataTable';
import ExportButton from '../shared/ExportButton';
import QueryStatusView from '../shared/QueryStatusView';
import MetaBanner from '../shared/MetaBanner';
import { useMonitoringQuery } from '../shared/useMonitoringQuery';
import { useMonitoringApiRef } from '../shared/useScopes';
import { useServerTable } from '../shared/useServerTable';
import { toCommonParams, type MonitoringFilters } from '../shared/useMonitoringFilters';
import { useFormat } from '../shared/format';

const { Title } = Typography;

export type MonitoringTabProps = {
    filters: MonitoringFilters;
    ready: boolean;
    active: boolean;
    refreshToken: number;
};

/** Raisons pour lesquelles les graphiques n'ont rien à montrer. */
const NO_CHART_REASONS = ['future_period', 'no_activity', 'no_students'] as const;

const PedagogyTab: React.FC<MonitoringTabProps> = ({ filters, ready, active, refreshToken }) => {
    const { t } = useTranslation();
    const f = useFormat();
    const apiRef = useMonitoringApiRef();
    const common = toCommonParams(filters);
    const table = useServerTable(JSON.stringify(common));

    const params: PedagogyParams = { ...common, students: table.query };
    const query = useMonitoringQuery<PedagogyResponse>(
        JSON.stringify(['pedagogy', params]),
        (signal) => apiRef.current.getPedagogy(params, signal),
        { refreshToken, enabled: active && ready, groupKey: JSON.stringify(['pedagogy', common]) }
    );
    const data = query.data;

    const count = (n: number) => <CompactValue value={n} />;
    const asOf = data?.meta.period.as_of;
    const tiles = data?.tiles;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <QueryStatusView query={query} tiles={9} />
            {data && tiles && (
                <>
                    <MetaBanner meta={data.meta} />
                    <EmptyState reasons={data.empty_reasons} />

                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-01" icon={<TeamOutlined />} asOf={asOf} value={count(tiles.students_followed)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-02" icon={<LoginOutlined />} value={count(tiles.students_connected)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-03" icon={<CheckCircleOutlined />} value={count(tiles.students_active)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-04" value={<RatioText ratio={tiles.engagement} showFraction />} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-05" icon={<BookOutlined />} value={count(tiles.lessons_completed)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile
                                metricId="PED-06"
                                icon={<ExperimentOutlined />}
                                value={count(tiles.exercises_submitted.value)}
                                extra={t('monitoring.pedagogy.lastStateShare', {
                                    n: f.compact(tiles.exercises_submitted.of_which_last_state).text,
                                })}
                            />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-07" icon={<ProjectOutlined />} value={count(tiles.projects_created)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-08" value={count(tiles.sessions)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="PED-09" icon={<FieldTimeOutlined />} value={f.minutes(tiles.active_minutes)} />
                        </Col>
                    </Row>

                    {!hasEmptyReason(data.empty_reasons, NO_CHART_REASONS) && (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.activity')}
                                    subtitle={t('monitoring.charts.activitySub')}
                                    series={data.series.filter((s) => ['PED-05', 'PED-06', 'PED-07'].includes(s.metric))}
                                    formatValue={(v) => f.compact(Number(v)).text}
                                />
                            </Col>
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.activeStudents')}
                                    subtitle={t('monitoring.charts.activeStudentsSub')}
                                    kind="area"
                                    series={data.series.filter((s) => s.metric === 'PED-03')}
                                    formatValue={(v) => f.compact(Number(v)).text}
                                />
                            </Col>
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.sessions')}
                                    series={data.series.filter((s) => s.metric === 'PED-08')}
                                    formatValue={(v) => f.compact(Number(v)).text}
                                />
                            </Col>
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.activeTime')}
                                    subtitle={t('metric.PED-09.help')}
                                    series={data.series.filter((s) => s.metric === 'PED-09')}
                                    formatValue={(v) => f.minutes(Number(v))}
                                />
                            </Col>
                        </Row>
                    )}

                    <Card style={cardStyle} title={<Title level={5} style={{ margin: 0 }}>{t('metric.PED-10.label')}</Title>}>
                        <DataTable table={data.subjects} rowKey={(row) => String(row.subject)} />
                    </Card>
                </>
            )}

            {data && (
                <Card
                    style={cardStyle}
                    title={
                        <Space size={12} wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Title level={5} style={{ margin: 0 }}>
                                {t('monitoring.table.students')}
                            </Title>
                            <Space size={8} wrap>
                                <Segmented
                                    size="small"
                                    aria-label={t('monitoring.col.status')}
                                    value={table.filterValues.status ?? 'all'}
                                    onChange={(v) => table.setFilter('status', v === 'all' ? undefined : String(v))}
                                    options={[
                                        { label: t('monitoring.filters.all'), value: 'all' },
                                        ...STUDENT_STATUSES.map((code) => ({
                                            label: t(`monitoring.enum.student_status.${code}`),
                                            value: code,
                                        })),
                                    ]}
                                />
                                <Input.Search
                                    allowClear
                                    size="small"
                                    style={{ width: 220 }}
                                    aria-label={t('monitoring.table.search')}
                                    placeholder={t('monitoring.table.search')}
                                    defaultValue={table.q}
                                    onSearch={(value) => table.setQuery(value.trim())}
                                />
                                <ExportButton
                                    dataset="pedagogy_students"
                                    params={{ ...common, table: table.filters }}
                                />
                            </Space>
                        </Space>
                    }
                >
                    <DataTable<PedagogyStudentRow>
                        table={data.students}
                        rowKey={(row) => String(row.id)}
                        loading={query.status === 'refreshing'}
                        server={{ state: table.state, onChange: table.setState }}
                        emptyText={t('monitoring.noStudents')}
                    />
                </Card>
            )}
        </Space>
    );
};

export default PedagogyTab;
